import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { Preference, PreferenceName } from '../../types';
import { PreferenceClient } from '../../client/preference.client';

interface UniquePreferencesContextProps {
  allUniquePreferences: Record<string, Preference[]>;
  refreshPreferences: () => Promise<void>;
  updatePreferences: (newPreference: Preference, preferenceName: PreferenceName) => void;
  deletePreferences: (deletedPreferenceId: string, preferenceName: PreferenceName) => void;
}

const UniquePreferencesContext = createContext<UniquePreferencesContextProps | undefined>(undefined);

export const UniquePreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [allUniquePreferences, setAllUniquePreferences] = useState<Record<string, Preference[]>>();

  const fetchAllUniquePreferences = useCallback(async () => {
    const results = await Promise.allSettled([
      PreferenceClient.getInstance().getAllUnique(PreferenceName.segment_price, true),
      PreferenceClient.getInstance().getAllUnique(PreferenceName.segment_feature, true),
    ]);

    const segmentPricePreferences = results[0].status === 'fulfilled' ? results[0].value : [];
    const segmentFeaturePreferences = results[1].status === 'fulfilled' ? results[1].value : [];

    setAllUniquePreferences({
      [PreferenceName.segment_price]: segmentPricePreferences,
      [PreferenceName.segment_feature]: segmentFeaturePreferences,
    });
  }, []);

  useEffect(() => {
    fetchAllUniquePreferences();
  }, [fetchAllUniquePreferences]);

  const updatePreferences = (newPreference: Preference, preferenceName: PreferenceName) => {
    if (!allUniquePreferences) {
      return;
    }
    const newPreferencesByPreferenceName = [...allUniquePreferences[preferenceName], newPreference];
    setAllUniquePreferences({ ...allUniquePreferences, [preferenceName]: newPreferencesByPreferenceName });
  };

  const deletePreferences = (deletedPreferenceId: string, preferenceName: PreferenceName) => {
    if (!allUniquePreferences) {
      return;
    }
    const newPreferencesByPreferenceName = allUniquePreferences[preferenceName].filter(
      preference => preference.id !== deletedPreferenceId
    );
    setAllUniquePreferences({ ...allUniquePreferences, [preferenceName]: newPreferencesByPreferenceName });
  };
  const contextValue = useMemo(
    () => ({
      allUniquePreferences,
      refreshPreferences: fetchAllUniquePreferences,
      updatePreferences,
      deletePreferences,
    }),
    [allUniquePreferences, fetchAllUniquePreferences, updatePreferences, deletePreferences]
  );

  return <UniquePreferencesContext.Provider value={contextValue}>{children}</UniquePreferencesContext.Provider>;
};

export const useUniquePreferences = (): UniquePreferencesContextProps => {
  const uniquePreferences = useContext(UniquePreferencesContext);
  if (!uniquePreferences) {
    return {
      allUniquePreferences: {},
      refreshPreferences: async () => {},
      updatePreferences: () => {},
      deletePreferences: () => {},
    };
  }
  return uniquePreferences;
};
